import { useState, useEffect } from 'react';
import api from '../services/api';
import { Gift, Plus, Search, CheckCircle2, XCircle, CreditCard, X, RefreshCw } from 'lucide-react';

export default function GiftCards() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newCard, setNewCard] = useState({ code: '', balance: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const loadCards = async () => {
    setLoading(true);
    try {
      const res = await api.get('/giftcards');
      setCards(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCards(); }, []);

  const generateCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 12; i++) {
      if (i > 0 && i % 4 === 0) code += '-';
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewCard({ ...newCard, code });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newCard.code || !newCard.balance) return setError('Code and balance are required');
    
    setIsSubmitting(true);
    setError('');
    try {
      await api.post('/giftcards', newCard);
      setShowAddModal(false);
      setNewCard({ code: '', balance: '' });
      loadCards();
    } catch (err) {
      setError(err.response?.data?.error || 'Error creating gift card');
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredCards = cards.filter(c => 
    c.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalActive = cards.filter(c => c.status === 'ACTIVE').length;
  const totalBalance = cards.filter(c => c.status === 'ACTIVE').reduce((sum, c) => sum + c.balance, 0);

  return (
    <div className="space-y-8 animate-fade-in-up pb-10">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h2 className="text-3xl font-black text-theme-main uppercase tracking-tight flex items-center gap-3">
            <Gift size={32} className="text-brand" /> Gift Cards
          </h2>
          <p className="text-theme-muted mt-1 font-bold uppercase tracking-widest text-xs">Manage customer gift cards and balances.</p>
        </div>
        <button 
          onClick={() => { setShowAddModal(true); generateCode(); }}
          className="bg-brand hover:bg-brand-light text-white px-6 py-3 rounded-2xl shadow-lg shadow-brand/20 font-black text-xs uppercase tracking-widest flex items-center gap-2 transition-all active:scale-95 whitespace-nowrap"
        >
          <Plus size={18} /> Generate Card
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-theme-card p-6 rounded-3xl border border-theme shadow-soft flex items-center gap-5">
          <div className="p-4 bg-brand/10 text-brand rounded-2xl">
            <CreditCard size={28} />
          </div>
          <div>
            <p className="text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] mb-1">Active Cards</p>
            <h4 className="text-3xl font-black text-theme-main tracking-tight">{totalActive}</h4>
          </div>
        </div>
        <div className="bg-theme-card p-6 rounded-3xl border border-theme shadow-soft flex items-center gap-5">
          <div className="p-4 bg-emerald-500/10 text-emerald-500 rounded-2xl">
            <Gift size={28} />
          </div>
          <div>
            <p className="text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] mb-1">Total Outstanding Balance</p>
            <h4 className="text-3xl font-black text-theme-main tracking-tight">${totalBalance.toFixed(2)}</h4>
          </div>
        </div>
      </div>

      <div className="bg-theme-card p-6 rounded-[2rem] border border-theme shadow-soft flex items-center gap-4">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-theme-muted" />
          <input 
            type="text" 
            placeholder="Search by card code..." 
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full bg-theme-main border border-theme rounded-xl py-3 pl-12 pr-4 text-theme-main font-bold outline-none focus:ring-2 focus:ring-brand transition-all uppercase placeholder-normal"
          />
        </div>
      </div>

      <div className="bg-theme-card rounded-[2.5rem] border border-theme shadow-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-theme-main/10 border-b border-theme text-[10px] font-black uppercase tracking-widest text-theme-muted">
                <th className="px-8 py-5">Code</th>
                <th className="px-6 py-5">Balance</th>
                <th className="px-6 py-5">Status</th>
                <th className="px-8 py-5">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-theme">
              {loading ? (
                <tr>
                  <td colSpan="4" className="px-8 py-10 text-center text-theme-muted text-sm font-bold">Loading...</td>
                </tr>
              ) : filteredCards.map(card => (
                <tr key={card.id} className="hover:bg-theme-main/5 transition-colors group">
                  <td className="px-8 py-5">
                    <span className="font-mono font-black text-brand tracking-widest bg-brand/10 px-3 py-1.5 rounded-lg border border-brand/20">
                      {card.code}
                    </span>
                  </td>
                  <td className="px-6 py-5">
                    <span className="text-sm font-black text-theme-main">${card.balance.toFixed(2)}</span>
                  </td>
                  <td className="px-6 py-5">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
                      card.status === 'ACTIVE' && card.balance > 0 ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20' : 
                      'bg-rose-500/10 text-rose-500 border border-rose-500/20'
                    }`}>
                      {card.status === 'ACTIVE' && card.balance > 0 ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
                      {card.balance <= 0 ? 'Exhausted' : card.status}
                    </span>
                  </td>
                  <td className="px-8 py-5 text-xs font-bold text-theme-muted">
                    {new Date(card.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
              {!loading && filteredCards.length === 0 && (
                <tr>
                  <td colSpan="4" className="px-8 py-16 text-center">
                    <Gift size={48} className="text-theme-muted opacity-20 mx-auto mb-4" />
                    <h3 className="text-xl font-black text-theme-main uppercase tracking-tight mb-1">No Cards Found</h3>
                    <p className="text-theme-muted text-sm font-medium">Try generating a new gift card.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-gray-900/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-theme-card w-full max-w-md rounded-[2.5rem] shadow-2xl border border-theme overflow-hidden flex flex-col transform transition-all animate-scale-up">
            <div className="px-8 py-6 border-b border-theme bg-theme-main/10 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-brand/10 text-brand rounded-xl"><Plus size={20} /></div>
                <h3 className="text-xl font-black text-theme-main uppercase tracking-tight">New Gift Card</h3>
              </div>
              <button onClick={() => setShowAddModal(false)} className="text-theme-muted hover:text-theme-main transition-colors bg-theme-main p-2 rounded-xl border border-theme"><X size={20} /></button>
            </div>
            
            <form onSubmit={handleCreate} className="p-8 space-y-6">
              {error && (
                <div className="bg-rose-500/10 text-rose-500 p-4 rounded-xl flex items-center gap-3 text-xs font-bold border border-rose-500/20">
                  <XCircle size={16} /> {error}
                </div>
              )}
              
              <div className="space-y-2">
                <label className="text-[10px] font-black text-theme-muted uppercase tracking-widest ml-1 flex justify-between">
                  <span>Card Code</span>
                  <button type="button" onClick={generateCode} className="text-brand flex items-center gap-1 hover:underline">
                    <RefreshCw size={10} /> Auto-Gen
                  </button>
                </label>
                <input 
                  type="text" 
                  value={newCard.code}
                  onChange={(e) => setNewCard({...newCard, code: e.target.value.toUpperCase()})}
                  className="w-full bg-theme-main border border-theme rounded-xl py-4 px-5 text-theme-main font-mono font-black text-center tracking-widest outline-none focus:ring-2 focus:ring-brand transition-all uppercase"
                  placeholder="XXXX-XXXX-XXXX"
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black text-theme-muted uppercase tracking-widest ml-1">Initial Balance ($)</label>
                <input 
                  type="number" 
                  step="0.01"
                  min="1"
                  value={newCard.balance}
                  onChange={(e) => setNewCard({...newCard, balance: e.target.value})}
                  className="w-full bg-theme-main border border-theme rounded-xl py-4 px-5 text-theme-main font-black outline-none focus:ring-2 focus:ring-brand transition-all"
                  placeholder="0.00"
                  required
                />
              </div>

              <div className="pt-4 flex gap-4">
                <button 
                  type="button" 
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 py-4 bg-theme-main text-theme-main border border-theme rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-theme-main/80 transition-all"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="flex-1 py-4 bg-brand hover:bg-brand-light text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg shadow-brand/20 transition-all disabled:opacity-50"
                >
                  {isSubmitting ? 'Generating...' : 'Create Card'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
